import { z } from "zod";

/**
 * Bookings and the waitlist (`RESERVATION`).
 *
 * Two shapes rather than one with a flag, for the same reason there are two
 * tables: a reservation is a promise about a future time and a waitlist entry
 * is a queue position now. They share a guest and nothing else.
 */

/**
 * A party size a restaurant can actually seat.
 *
 * Bounded at both ends deliberately. Zero is a typo that would otherwise sit in
 * a book looking like a booking, and the ceiling is high enough for a private
 * function and low enough that a mis-keyed phone number cannot become a party
 * of four hundred.
 */
const PartySize = z.number().int().min(1).max(200);

export const ReservationStatusSchema = z.enum([
  "booked",
  "seated",
  "completed",
  "no_show",
  "cancelled",
]);

export const ReservationSourceSchema = z.enum(["phone", "walk_in", "online", "third_party"]);

export const WaitlistStatusSchema = z.enum(["waiting", "notified", "seated", "left"]);

export const ReservationSchema = z.object({
  id: z.uuid(),
  guestName: z.string(),
  guestPhone: z.string().nullable(),
  consumerId: z.uuid().nullable(),
  partySize: z.number().int(),
  startsAt: z.string(),
  durationMinutes: z.number().int(),
  tableId: z.uuid().nullable(),
  tableName: z.string().nullable(),
  status: ReservationStatusSchema,
  source: ReservationSourceSchema,
  orderId: z.uuid().nullable(),
  note: z.string().nullable(),
  seatedAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  createdAt: z.string(),
});

export const SaveReservationSchema = z.object({
  guestName: z.string().min(1).max(120),
  guestPhone: z.string().max(40).nullable().default(null),
  consumerId: z.uuid().nullable().default(null),
  partySize: PartySize,
  startsAt: z.string(),
  durationMinutes: z.number().int().min(15).max(600).default(90),
  tableId: z.uuid().nullable().default(null),
  source: ReservationSourceSchema.default("phone"),
  note: z.string().max(500).nullable().default(null),
});

/**
 * Written longhand rather than as `.partial()`.
 *
 * `.partial()` keeps a field's `.default()`, so a host editing only the note
 * would silently reset `durationMinutes` to 90 and `source` to `phone` —
 * `patch-schemas.test.ts` guards exactly this, and it has shipped three times.
 */
export const PatchReservationSchema = z.object({
  guestName: z.string().min(1).max(120).optional(),
  guestPhone: z.string().max(40).nullable().optional(),
  partySize: PartySize.optional(),
  startsAt: z.string().optional(),
  durationMinutes: z.number().int().min(15).max(600).optional(),
  tableId: z.uuid().nullable().optional(),
  source: ReservationSourceSchema.optional(),
  note: z.string().max(500).nullable().optional(),
});

/**
 * Seating takes the table, not the booking's guess at one.
 *
 * A host assigns a table hours ahead and moves it twice before the party
 * arrives. Taking the table at the moment somebody sits is the only version
 * that matches what happened.
 */
export const SeatReservationSchema = z.object({
  tableId: z.uuid().nullable().default(null),
});

export const CancelReservationSchema = z.object({
  reason: z.string().min(1).max(500),
});

/** The day a host is looking at, in the store's own timezone. */
export const ReservationQuerySchema = z.object({
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  status: ReservationStatusSchema.optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce.number().int().min(1).max(200).default(50),
});

export const WaitlistEntrySchema = z.object({
  id: z.uuid(),
  guestName: z.string(),
  guestPhone: z.string().nullable(),
  partySize: z.number().int(),
  quotedMinutes: z.number().int().nullable(),
  status: WaitlistStatusSchema,
  notifiedAt: z.string().nullable(),
  seatedAt: z.string().nullable(),
  tableId: z.uuid().nullable(),
  tableName: z.string().nullable(),
  orderId: z.uuid().nullable(),
  note: z.string().nullable(),
  /** Minutes since they joined, which is the number the host is reading. */
  waitingMinutes: z.number().int(),
  createdAt: z.string(),
});

export const JoinWaitlistSchema = z.object({
  guestName: z.string().min(1).max(120),
  guestPhone: z.string().max(40).nullable().default(null),
  partySize: PartySize,
  quotedMinutes: z.number().int().min(0).max(600).nullable().default(null),
  note: z.string().max(500).nullable().default(null),
});

export const PatchWaitlistEntrySchema = z.object({
  guestName: z.string().min(1).max(120).optional(),
  guestPhone: z.string().max(40).nullable().optional(),
  partySize: PartySize.optional(),
  quotedMinutes: z.number().int().min(0).max(600).nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});

export const WaitlistQuerySchema = z.object({
  status: WaitlistStatusSchema.optional(),
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  size: z.coerce.number().int().min(1).max(200).default(50),
});

export type Reservation = z.infer<typeof ReservationSchema>;
export type WaitlistEntry = z.infer<typeof WaitlistEntrySchema>;
